import { Button, Grid, Link, Typography } from "@mui/material";
import { useForm } from "react-hook-form";
import { useEffect, useState } from "react";
import { Box } from "@mui/system";
import { LoadingButton } from "@mui/lab";
import { apiConstants, postMultipart, getGeneric } from "../../../common/constants";
import { FormProvider, RHFDateField, RHFSelectField, RHFTextField } from "../../../common/hook-form";
import { useSnackbar } from "notistack";
import { formatDate2 } from "../../../helpers/UtilityHelper";
import dayjs from "dayjs";
import * as Yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import ExtraDocsEditor, { collectExtraDocs, categoryLabel } from "../ExtraDocsEditor";

// additionalDocuments field per docType (Non-ABD upload).
const ADDITIONAL_DOC_KEYS = { INVOICE: 'invoiceS3Key', EWAY_BILL: 'ewayBillS3Key', PO_COPY: 'poCopyS3Key' };

function AppointmentDelivery(props) {
    const [isSubmitting, setIsSubmitting] = useState(false)
    const { enqueueSnackbar } = useSnackbar();
    const { rowData, setDelConfirm, setReload } = props;
    const timeSlots = [
        { value: '8-10AM', label: '8-10 AM' },
        { value: '10-12PM', label: '10-12 PM' },
        { value: '12-2PM', label: '12-2 PM' },
    ];

    const phoneRegex = /^[0-9]{10}$/;
    const AppointmentSchema = Yup.object().shape({
        appointmentDate: Yup.date().required('Delivery date is required'),
        timeSlot: Yup.string().required('Time slot is required'),
        appointment_del_po: Yup.string().required('PO Detail is required'),
        appointment_del_po_expiry: Yup.date().required('PO Expiry Date is required'),
        appointment_del_alt_contact_1: Yup.string().nullable().notRequired().test('phone1', 'Must be 10 digits', v => !v || phoneRegex.test(v)),
        appointment_del_alt_contact_2: Yup.string().nullable().notRequired().test('phone2', 'Must be 10 digits', v => !v || phoneRegex.test(v)),
    });

    const methods = useForm({
        resolver: yupResolver(AppointmentSchema),
        defaultValues: {
            timeSlot: '',
            appointmentDate: null,
            appointment_del_po: '',
            appointment_del_po_expiry: null,
            appointment_del_remarks: '',
            appointment_del_alt_contact_1: '',
            appointment_del_alt_contact_2: '',
        }
    });
    const { handleSubmit, reset, setValue } = methods
    const [files, setFiles] = useState({ invoice: null, eway_bill: null, po_copy: null });
    const [extraDocRows, setExtraDocRows] = useState([]);

    useEffect(() => {
        // Editor holds NEW docs only; already-saved extras are shown read-only (append-only).
        setExtraDocRows([]);
        if (rowData?.appointmentDelivery && rowData?.appointmentDeliveryData) {
            const appointmentData = rowData?.appointmentDeliveryData;
            if (appointmentData?.appointment_del_start) {
                const startHour = dayjs(appointmentData?.appointment_del_start).hour();
                let timeSlotValue = '';
                if (startHour === 8) {
                    timeSlotValue = '8-10AM';
                } else if (startHour === 10) {
                    timeSlotValue = '10-12PM';
                } else if (startHour === 12) {
                    timeSlotValue = '12-2PM';
                }
                setValue('appointmentDate', dayjs(appointmentData?.appointment_del_start).toDate());
                setValue('timeSlot', timeSlotValue);
            }
            if (appointmentData?.appointment_del_po) {
                setValue('appointment_del_po', appointmentData?.appointment_del_po);
            }
            if (appointmentData?.appointment_del_po_expiry) {
                setValue('appointment_del_po_expiry', dayjs(appointmentData?.appointment_del_po_expiry).toDate());
            }
            if (appointmentData?.appointment_del_remarks) {
                setValue('appointment_del_remarks', appointmentData?.appointment_del_remarks);
            }
            if (appointmentData?.appointment_del_alt_contact_1) {
                setValue('appointment_del_alt_contact_1', appointmentData.appointment_del_alt_contact_1);
            }
            if (appointmentData?.appointment_del_alt_contact_2) {
                setValue('appointment_del_alt_contact_2', appointmentData.appointment_del_alt_contact_2);
            }
        }
    }, [rowData]);

    const onSubmit = async (data) => {
        setIsSubmitting(true)
        try {
            let startTime = null;
            let endTime = null;
            if (data.timeSlot === '8-10AM') {
                startTime = '08:00:00';
                endTime = '10:00:00';
            } else if (data.timeSlot === '10-12PM') {
                startTime = '10:00:00';
                endTime = '12:00:00';
            } else if (data.timeSlot === '12-2PM') {
                startTime = '12:00:00';
                endTime = '14:00:00';
            }

            const fd = new FormData();
            const { extraDocs, error } = collectExtraDocs(extraDocRows, fd);
            if (error) {
                enqueueSnackbar(error, { variant: "error" });
                setIsSubmitting(false);
                return;
            }

            const payload = {
                "awbNumber": rowData?.awb_number,
                "appointmentDeliveryDate": formatDate2(data.appointmentDate, 'yyyy MMM, dd'),
                "data": {
                    "appointment_del_start": data.appointmentDate && startTime
                        ? `${formatDate2(data.appointmentDate, 'yyyy MMM, dd')}T${startTime}`
                        : null,
                    "appointment_del_end": data.appointmentDate && endTime
                        ? `${formatDate2(data.appointmentDate, 'yyyy MMM, dd')}T${endTime}`
                        : null,
                    "appointment_del_po": data.appointment_del_po,
                    "appointment_del_po_expiry": formatDate2(data.appointment_del_po_expiry, 'yyyy MMM, dd'),
                    "appointment_del_remarks": data.appointment_del_remarks,
                    "appointment_del_alt_contact_1": data.appointment_del_alt_contact_1 || null,
                    "appointment_del_alt_contact_2": data.appointment_del_alt_contact_2 || null,
                    "appointment_del_extra_docs": [...existingExtra, ...extraDocs],
                }
            };
            fd.append('data', new Blob([JSON.stringify(payload)], { type: 'application/json' }));
            if (files.invoice) fd.append('invoice', files.invoice);
            if (files.eway_bill) fd.append('eway_bill', files.eway_bill);
            if (files.po_copy) fd.append('po_copy', files.po_copy);
            await postMultipart(apiConstants.APPOINTMENT_DELIVERY, fd);
            setDelConfirm(false)
            enqueueSnackbar("Delivery scheduled", { variant: "success" });
            setReload(reload => reload + 1)
            reset()
        }
        catch (err) {
            console.log(err)
            enqueueSnackbar(err?.response?.data?.message || "Something went wrong", { variant: "error" });
        }
        setIsSubmitting(false)
    }
    const getMinDate = () => {
        const tomorrow = dayjs().add(1, 'day').startOf('day');
        if (rowData?.edd) {
            const eddPlusOne = dayjs(rowData.edd).add(1, 'day').startOf('day');
            // Return the later date between tomorrow and edd+1
            return eddPlusOne.isAfter(tomorrow) ? eddPlusOne.toDate() : tomorrow.toDate();
        }
        return tomorrow.toDate();
    };

    const docs = [
        { key: 'invoice', label: 'Upload Invoice', keyField: 'appointment_del_invoice_s3_key', docType: 'INVOICE' },
        { key: 'eway_bill', label: 'Upload E-Way Bill', keyField: 'appointment_del_eway_bill_s3_key', docType: 'EWAY_BILL' },
        { key: 'po_copy', label: 'Upload PO Copy', keyField: 'appointment_del_po_copy_s3_key', docType: 'PO_COPY' },
    ];

    const activeAppt = rowData?.appointmentDelivery && rowData?.appointmentDeliveryData
        ? rowData.appointmentDeliveryData
        : null;
    const lastClosed = (rowData?.appointmentDeliveryHistory || []).slice(-1)[0];
    const lastClosedIndex = (rowData?.appointmentDeliveryHistory || []).length - 1;
    const existingExtra = (activeAppt || lastClosed)?.appointment_del_extra_docs || [];
    const extraDocsHistoryIndex = activeAppt ? null : lastClosedIndex;
    // Documents uploaded before any appointment (Non-ABD upload), shown for viewing as in the Admin Panel.
    const additional = rowData?.additionalDocuments;
    const additionalExtra = additional?.extraDocs || [];

    const fetchDocUrl = async (docType, historyIndex) => {
        try {
            const params = { awbNumber: rowData?.awb_number, docType };
            if (historyIndex != null && historyIndex >= 0) params.historyIndex = historyIndex;
            const res = await getGeneric(apiConstants.APPOINTMENT_DELIVERY_DOC_URL, params);
            const url = res?.data?.responseObj?.url || res?.data?.url;
            if (url) window.open(url, '_blank');
            else enqueueSnackbar("Document URL missing", { variant: "warning" });
        } catch (err) {
            enqueueSnackbar(err?.response?.data?.message || "Failed to fetch document", { variant: "error" });
        }
    };

    const fetchAdditionalDocUrl = async (params) => {
        try {
            const res = await getGeneric(apiConstants.ADDITIONAL_DOCUMENTS_DOC_URL, { awbNumber: rowData?.awb_number, ...params });
            const url = res?.data?.responseObj?.url || res?.data?.url;
            if (url) window.open(url, '_blank');
            else enqueueSnackbar("Document URL missing", { variant: "warning" });
        } catch (err) {
            enqueueSnackbar(err?.response?.data?.message || "Failed to fetch document", { variant: "error" });
        }
    };

    const fetchExtraDocUrl = async (doc) => {
        try {
            const params = { awbNumber: rowData?.awb_number, docId: doc?.docId };
            if (extraDocsHistoryIndex != null && extraDocsHistoryIndex >= 0) params.historyIndex = extraDocsHistoryIndex;
            const res = await getGeneric(apiConstants.APPOINTMENT_DELIVERY_DOC_URL, params);
            const url = res?.data?.responseObj?.url || res?.data?.url;
            if (url) window.open(url, '_blank');
            else enqueueSnackbar("Document URL missing", { variant: "warning" });
        } catch (err) {
            enqueueSnackbar(err?.response?.data?.message || "Failed to fetch document", { variant: "error" });
        }
    };

    return (
        <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
            <Typography variant="body2" color={'error'}>
                Note: If appointment is after 2 PM, please contact with your KAM
            </Typography>
            <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={6}>
                    <RHFDateField
                        minDate={getMinDate()}
                        name='appointmentDate'
                        label={rowData?.edd ? `Delivery Date (EDD : ${dayjs(rowData?.edd).format('DD MMM, YYYY')}) *` : 'Delivery Date *'}
                    />
                </Grid>
                <Grid item xs={6}>
                    <RHFSelectField
                        width="100%"
                        name="timeSlot"
                        label={`Select Time Slot *`}
                        options={timeSlots}
                    />
                </Grid>
                <Grid item xs={6}>
                    <RHFTextField name="appointment_del_po" label="PO *" color="secondary" variant="filled" />
                </Grid>
                <Grid item xs={6}>
                    <RHFDateField disablePast name='appointment_del_po_expiry' label='PO Expiry Date *' />
                </Grid>
                <Grid item xs={6}>
                    <RHFTextField name="appointment_del_remarks" label="Remarks" color="secondary" variant="filled" multiline />
                </Grid>
                <Grid item xs={6}>
                    <RHFTextField name="appointment_del_alt_contact_1" label="Alternate Receiver Contact 1" color="secondary" variant="filled" />
                </Grid>
                <Grid item xs={6}>
                    <RHFTextField name="appointment_del_alt_contact_2" label="Alternate Receiver Contact 2" color="secondary" variant="filled" />
                </Grid>
                <Grid item xs={12}>
                    <Grid container spacing={2}>
                        {docs.map(({ key, label, keyField, docType }) => {
                            // Same order as the Admin Panel: active appointment, then the pre-appointment upload
                            // (newer than a closed appointment's copy), then the last closed appointment.
                            const activeKey = activeAppt?.[keyField];
                            const additionalKey = !activeKey && additional?.[ADDITIONAL_DOC_KEYS[docType]];
                            const closedKey = lastClosed?.[keyField];
                            const existingKey = activeKey || additionalKey || closedKey;
                            const onViewExisting = additionalKey
                                ? () => fetchAdditionalDocUrl({ docType })
                                : () => fetchDocUrl(docType, activeKey ? null : lastClosedIndex);
                            const selected = files[key];
                            return (
                                <Grid item xs={12} sm={4} key={key}>
                                    <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1.5, height: '100%' }}>
                                        <Typography variant="caption" display="block" sx={{ mb: 0.5, fontWeight: 500 }}>{label}</Typography>
                                        <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                                            <Button variant="outlined" size="small" component="label">
                                                {selected || existingKey ? 'Replace' : 'Choose File'}
                                                <input type="file" hidden onChange={(e) => setFiles(prev => ({ ...prev, [key]: e.target.files?.[0] || null }))} />
                                            </Button>
                                            {!selected && existingKey && (
                                                <Link component="button" type="button" variant="caption" onClick={onViewExisting}>View</Link>
                                            )}
                                        </Box>
                                        {selected && (
                                            <Typography variant="caption" noWrap display="block" sx={{ mt: 0.5, overflow: 'hidden', textOverflow: 'ellipsis' }} title={selected.name}>{selected.name}</Typography>
                                        )}
                                    </Box>
                                </Grid>
                            );
                        })}
                    </Grid>
                </Grid>
                <Grid item xs={12}>
                    {(existingExtra.length > 0 || additionalExtra.length > 0) && (
                        <Box sx={{ mb: 1.5 }}>
                            <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ textTransform: 'uppercase', letterSpacing: 0.4 }}>
                                Existing additional documents
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 0.5 }}>
                                {existingExtra.map((xd) => (
                                    <Button key={xd.docId} size="small" variant="text" onClick={() => fetchExtraDocUrl(xd)}>
                                        {xd.docLabel || categoryLabel(xd.docType)}
                                    </Button>
                                ))}
                                {additionalExtra.map((xd) => (
                                    <Button key={xd.docId} size="small" variant="text" onClick={() => fetchAdditionalDocUrl({ docId: xd.docId })}>
                                        {xd.docLabel || categoryLabel(xd.docType)}
                                    </Button>
                                ))}
                            </Box>
                        </Box>
                    )}
                    <ExtraDocsEditor rows={extraDocRows} setRows={setExtraDocRows} onView={fetchExtraDocUrl} existingCount={existingExtra.length} />
                </Grid>
            </Grid>
            <Grid xs={12} display="flex" justifyContent="flex-end" sx={{ paddingTop: 2 }}>
                <Box sx={{ paddingTop: 1, marginRight: 1 }} >
                    <Button
                        variant="outlined"
                        size="small"
                        onClick={() => setDelConfirm(false)}
                    >Close</Button>
                </Box>
                <Box sx={{ paddingTop: 1 }} >
                    <LoadingButton
                        loading={isSubmitting}
                        variant="contained"
                        size="small"
                        type="submit"
                    >Schedule</LoadingButton>
                </Box>
            </Grid>
        </FormProvider>
    )
}

export default AppointmentDelivery;
